import { z } from 'zod'

import {
  taskSchema,
  createTaskRequestSchema,
  updateTaskRequestSchema,
  reassignTaskRequestSchema,
  completeTaskRequestSchema,
  tasksFeedResponseSchema,
  taskActivitySchema,
  type Task,
  type CreateTaskRequest,
  type UpdateTaskRequest,
  type ReassignTaskRequest,
  type CompleteTaskRequest,
  type TaskFeedItem,
  type TasksFeedResponse,
  type TaskActivity,
} from '@/types/api/task'

import { apiClient } from './client'

export async function getTasksFeed(
  section: 'remaining' | 'completed' = 'remaining',
  dateRange?: { from?: string; to?: string }
): Promise<TasksFeedResponse> {
  const response = await apiClient.get('/tasks', {
    params: {
      section,
      from: dateRange?.from,
      to: dateRange?.to,
    },
  })
  return tasksFeedResponseSchema.parse(response.data)
}

export async function getTask(taskId: string): Promise<Task> {
  const response = await apiClient.get(`/tasks/${taskId}`)
  return taskSchema.parse(response.data)
}

export async function createTask(data: CreateTaskRequest): Promise<Task> {
  const validated = createTaskRequestSchema.parse(data)
  const response = await apiClient.post('/tasks', validated)
  return taskSchema.parse(response.data)
}

export async function updateTask(taskId: string, data: UpdateTaskRequest): Promise<Task> {
  const validated = updateTaskRequestSchema.parse(data)
  const response = await apiClient.put(`/tasks/${taskId}`, validated)
  return taskSchema.parse(response.data)
}

export async function deleteTask(taskId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}`)
}

export async function reassignTask(taskId: string, data: ReassignTaskRequest): Promise<Task> {
  const validated = reassignTaskRequestSchema.parse(data)
  const response = await apiClient.post(`/tasks/${taskId}/reassign`, validated)
  return taskSchema.parse(response.data)
}

export async function completeTask(taskId: string, data: CompleteTaskRequest): Promise<Task> {
  const validated = completeTaskRequestSchema.parse(data)
  const response = await apiClient.post(`/tasks/${taskId}/complete`, validated)
  return taskSchema.parse(response.data)
}

export async function reopenTask(taskId: string, data: CompleteTaskRequest): Promise<Task> {
  const validated = completeTaskRequestSchema.parse(data)
  const response = await apiClient.post(`/tasks/${taskId}/reopen`, validated)
  return taskSchema.parse(response.data)
}

export async function getTaskActivities(taskId: string, page: number = 0, size: number = 20): Promise<TaskActivity[]> {
  const response = await apiClient.get(`/tasks/${taskId}/activities`, { params: { page, size } })
  return z.array(taskActivitySchema).parse(response.data)
}

export async function getWorkspaceUsers(): Promise<{ userId: string; name: string; email: string }[]> {
  const response = await apiClient.get('/workspace-users')
  return response.data
}

// Record follow-up completion/reopen
export async function markRecordFollowUpComplete(
  recordId: string,
  data: { recordActivitySessionId: string | null; note: string | null; nextActivityAt?: string | null; nextActivityInstructions?: string | null; tagId?: string | null; followUpTagId?: string | null }
): Promise<void> {
  const body: Record<string, unknown> = {
    recordActivitySessionId: data.recordActivitySessionId,
    note: data.note ?? null,
  }
  if (data.nextActivityAt) body.nextActivityAt = data.nextActivityAt
  if (data.nextActivityInstructions) body.nextActivityInstructions = data.nextActivityInstructions
  if (data.tagId) body.tagId = data.tagId
  if (data.followUpTagId) body.followUpTagId = data.followUpTagId
  await apiClient.post(`/activities/complete/record/${recordId}`, body)
}

export async function reopenRecordFollowUp(
  recordId: string,
  data: { recordActivitySessionId: string | null; note: string | null; nextActivityAt?: string | null; nextActivityInstructions?: string | null; tagId?: string | null; followUpTagId?: string | null }
): Promise<void> {
  const body: Record<string, unknown> = {
    recordActivitySessionId: data.recordActivitySessionId,
    note: data.note ?? null,
  }
  if (data.nextActivityAt) body.nextActivityAt = data.nextActivityAt
  if (data.nextActivityInstructions) body.nextActivityInstructions = data.nextActivityInstructions
  if (data.tagId) body.tagId = data.tagId
  if (data.followUpTagId) body.followUpTagId = data.followUpTagId
  await apiClient.post(`/activities/reopen/record/${recordId}`, body)
}

export type {
  Task,
  CreateTaskRequest,
  UpdateTaskRequest,
  ReassignTaskRequest,
  CompleteTaskRequest,
  TaskFeedItem,
  TasksFeedResponse,
  TaskActivity,
}
